import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export type SupportedCurrency = 'ZAR' | 'USD' | 'EUR' | 'GBP';

interface CurrencyInfo {
  symbol: string;
  name: string;
  decimals: number;
}

export const CURRENCY_INFO: Record<SupportedCurrency, CurrencyInfo> = {
  ZAR: { symbol: 'R', name: 'South African Rand', decimals: 2 },
  USD: { symbol: '$', name: 'US Dollar', decimals: 2 },
  EUR: { symbol: '€', name: 'Euro', decimals: 2 },
  GBP: { symbol: '£', name: 'British Pound', decimals: 2 },
};

interface CurrencyContextValue {
  currency: SupportedCurrency;
  setCurrency: (currency: SupportedCurrency) => void;
  getCurrencyInfo: (currency: SupportedCurrency) => CurrencyInfo;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState<SupportedCurrency>('ZAR');

  const getCurrencyInfo = useCallback(
    (curr: SupportedCurrency) => CURRENCY_INFO[curr],
    [],
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, getCurrencyInfo }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
